import { type ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { KeybindChip } from '../KeybindChip/KeybindChip';
import { OVERLAY_DESC_CLASS, OVERLAY_TITLE_CLASS } from './surface';

/**
 * Shared web body layout for `Tooltip` and `Popover` (Rich form).
 *
 * Renders, in Figma order (node 2203:6118):
 *   1. a header row — Bold title on the left, optional keybind keycap(s)
 *      ("Keyboard shortcut=Yes", node 2205:12888) and `headerRight` (the
 *      Popover dismiss "X") pinned right;
 *   2. optional `media` — an image / short video, clipped in an 8px-radius
 *      secondary-100-bordered frame (Figma "Media=Yes");
 *   3. the description body — a string/number gets DS description typography,
 *      JSX renders as-is (this is where a Popover's action button lives). In
 *      the Simple form (no header) the keycap(s) trail this row instead
 *      (node 2205:12868).
 *
 * Web-only (`<p>` / `<div>`); the native equivalent is baked into
 * `FloatingBox.native` (which never renders keycaps — see
 * `TooltipProps['keybinding']`).
 */
export function OverlayBody({
  title,
  media,
  headerRight,
  keybinding,
  children,
}: {
  title?: ReactNode;
  media?: ReactNode;
  /** Trailing header slot — the Popover dismiss "X". Tooltip never sets it. */
  headerRight?: ReactNode;
  /** Keycap(s) per Figma "Keyboard shortcut=Yes". See TooltipProps. */
  keybinding?: string | string[];
  children?: ReactNode;
}) {
  const hasHeader = title != null || headerRight != null;
  const keys = (
    keybinding == null ? [] : Array.isArray(keybinding) ? keybinding : [keybinding]
  ).filter((k) => k !== '');
  const chips =
    keys.length > 0 ? (
      // <div>: KeybindChip's View primitive renders a div on web.
      <div className="flex shrink-0 items-center gap-1">
        {keys.map((k, i) => (
          <KeybindChip key={`${k}-${i}`} variant="secondary" chipSize="sm">
            {k}
          </KeybindChip>
        ))}
      </div>
    ) : null;
  // Simple form (no header row): the keycaps trail the description row.
  const bodyChips = hasHeader ? null : chips;
  return (
    <>
      {hasHeader ? (
        // Figma header gap: 8px for title + "X" (2203:6118); 6px once the
        // keycap joins the row (2205:12893).
        <div
          className={cn(
            'flex w-full items-start justify-between',
            chips ? 'gap-1.5' : 'gap-2',
          )}>
          {title != null ? (
            <p className={`${OVERLAY_TITLE_CLASS} flex-1`}>{title}</p>
          ) : (
            <span className="flex-1" />
          )}
          {chips}
          {headerRight}
        </div>
      ) : null}
      {media != null ? (
        <div className="w-full overflow-hidden rounded-lg border border-secondary-100">
          {media}
        </div>
      ) : null}
      {children != null ? (
        bodyChips ? (
          // Simple + keybinding (2205:12873): description flex-1, keycap
          // trailing, 10px gap.
          <div className="flex w-full items-center gap-2.5">
            {typeof children === 'string' || typeof children === 'number' ? (
              <p className={`${OVERLAY_DESC_CLASS} min-w-0 flex-1`}>{children}</p>
            ) : (
              <div className="min-w-0 flex-1">{children}</div>
            )}
            {bodyChips}
          </div>
        ) : typeof children === 'string' || typeof children === 'number' ? (
          <p className={`${OVERLAY_DESC_CLASS} w-full`}>{children}</p>
        ) : (
          children
        )
      ) : (
        // Keybinding with no body (rare) still renders its row in Simple form.
        bodyChips && <div className="flex w-full justify-end">{bodyChips}</div>
      )}
    </>
  );
}
